import { registerCustomControlProvider } from '../registry';
import { buildObjectActionsButtons } from './shared';
import { showSidebarTab } from '~src/sidebarLegacy';
import {
  editorSetTextAlign,
  toggleFontStlyeItalic,
  fontSizePlus,
  fontSizeMinus,
  toggleFontWeightBold,
  toggleTextUnderline,
} from '~core/text';
import { toggleTextBorder } from '~core';

registerCustomControlProvider({
  id: 'text-floating-controls',
  match: (object) =>
    object && (object.type === 'textbox' || object.type === 'i-text'),
  create: (manager, textbox) => {
    let control;
    const setActive = () => {
      if (textbox && window.canvas) {
        window.canvas.setActiveObject(textbox);
        window.canvas.requestRenderAll();
      }
    };

    // Helpers defined before usage to satisfy linting
    const ensureSquares = () => {
      const root =
        control && typeof control.getElement === 'function'
          ? control.getElement()
          : null;
      if (!root) return;
      const colorBtn = root.querySelector('.btn-text-color');
      if (colorBtn && !colorBtn.querySelector('.text-color-square')) {
        const sq = document.createElement('div');
        sq.className = 'text-square text-color-square';
        colorBtn.appendChild(sq);
      }
      const borderBtn = root.querySelector('.btn-text-border');
      if (borderBtn && !borderBtn.querySelector('.text-border-square')) {
        const sq = document.createElement('div');
        sq.className = 'text-square text-border-square';
        borderBtn.appendChild(sq);
      }
    };

    function updateActiveStates() {
      const obj = window.canvas && window.canvas.getActiveObject();
      if (!obj || (obj.type !== 'textbox' && obj.type !== 'i-text')) return;
      const root =
        control && typeof control.getElement === 'function'
          ? control.getElement()
          : null;
      if (!root) return;
      ensureSquares();
      const getWeight = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.fontWeight || obj.fontWeight
          : obj.fontWeight;
      const isBold = (w) => w === 'bold' || parseInt(w) >= 600;
      const boldBtn = root.querySelector('.btn-text-bold');
      if (boldBtn) boldBtn.classList.toggle('active', isBold(getWeight()));

      const getUnderline = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.underline ?? obj.underline
          : obj.underline;
      const ulBtn = root.querySelector('.btn-text-underline');
      if (ulBtn) ulBtn.classList.toggle('active', !!getUnderline());

      const getItalic = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.fontStyle || obj.fontStyle
          : obj.fontStyle;
      const itBtn = root.querySelector('.btn-text-italic');
      if (itBtn) itBtn.classList.toggle('active', getItalic() === 'italic');

      const getAlign = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.textAlign || obj.textAlign
          : obj.textAlign;
      const currentAlign = getAlign() || 'left';
      const alignBtns = {
        left: root.querySelector('.btn-text-align-left'),
        center: root.querySelector('.btn-text-align-center'),
        right: root.querySelector('.btn-text-align-right'),
        justify: root.querySelector('.btn-text-align-justify'),
      };
      Object.entries(alignBtns).forEach(([key, el]) => {
        if (!el) return;
        el.classList.toggle('active', key === currentAlign);
      });

      try {
        const vRow = root.querySelector('.text-spacing-v-control');
        if (vRow) {
          const v = typeof obj.lineHeight === 'number' ? obj.lineHeight : 1;
          const range = vRow.querySelector('input.control-range');
          const num = vRow.querySelector('input.control-number');
          if (range) range.value = String(v);
          if (num) num.value = String(v);
        }
      } catch (e) {
        console.error('Failed updating V spacing values on state update', e);
      }
      try {
        const hRow = root.querySelector('.text-spacing-h-control');
        if (hRow) {
          const v = typeof obj.charSpacing === 'number' ? obj.charSpacing : 0;
          const range = hRow.querySelector('input.control-range');
          const num = hRow.querySelector('input.control-number');
          if (range) range.value = String(v);
          if (num) num.value = String(v);
        }
      } catch (e) {
        console.error('Failed updating H spacing values on state update', e);
      }
      try {
        const oRow = root.querySelector('.text-opacity-control');
        if (oRow) {
          const v = typeof obj.opacity === 'number' ? obj.opacity : 1;
          const range = oRow.querySelector('input.control-range');
          const num = oRow.querySelector('input.control-number');
          if (range) range.value = String(v);
          if (num) num.value = String(v);
        }
      } catch (e) {
        console.error('Failed updating opacity value on state update', e);
      }

      const getFontFamily = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.fontFamily || obj.fontFamily
          : obj.fontFamily;
      const fontBtn = root.querySelector('.btn-text-font');
      if (fontBtn) {
        const idx = parseInt(
          fontBtn.getAttribute('data-button-index') || '0',
          10
        );
        control.updateButton(idx, { text: getFontFamily() || 'Font' });
      }

      const getFontSize = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.fontSize || obj.fontSize
          : obj.fontSize;
      const sizeEl = root.querySelector('.btn-text-size-value');
      if (sizeEl) sizeEl.textContent = `${Math.round(getFontSize() || 0)}`;

      const getFill = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.fill || obj.fill
          : obj.fill;
      const colorSq = root.querySelector('.text-color-square');
      if (colorSq) colorSq.style.backgroundColor = getFill() || '#000';

      const getStroke = () =>
        obj.isEditing
          ? obj.getSelectionStyles()[0]?.stroke || obj.stroke
          : obj.stroke;
      const stroke = getStroke();
      const borderSq = root.querySelector('.text-border-square');
      if (borderSq) {
        const active = !!(
          stroke &&
          stroke !== 'transparent' &&
          stroke !== null
        );
        borderSq.style.borderColor = active ? stroke : '#cbd5e1';
        borderSq.classList.toggle('active', active);
      }

      if (typeof control.refreshIconColors === 'function') {
        control.refreshIconColors();
      }
    }

    const options = {
      offsetY: 0,
      offsetX: 0,
      gap: 12,
      initialView: 'home',
      views: {
        home: {
          layout: 'row',
          buttons: [
            {
              text: 'Font',
              className: 'btn-text-control btn-text-font',
              onClick: () => {
                setActive();
                showSidebarTab('font');
              },
            },
            {
              svg: '/assets/svg/testo-dimensione-diminuisci.svg',
              className: 'btn-text-control btn-text-size-minus',
              onClick: () => {
                setActive();
                fontSizeMinus();
                setTimeout(updateActiveStates, 0);
              },
            },
            { type: 'container', className: 'btn-text-size-value' },
            {
              svg: '/assets/svg/testo-dimensione-aumenta.svg',
              className: 'btn-text-control btn-text-size-plus',
              onClick: () => {
                setActive();
                fontSizePlus();
                setTimeout(updateActiveStates, 0);
              },
            },
            {
              className: 'btn-text-control btn-text-color',
              onClick: () => {
                setActive();
                showSidebarTab('colori');
              },
            },
            {
              className: 'btn-text-control btn-text-border',
              onClick: () => {
                setActive();
                toggleTextBorder();
                setTimeout(updateActiveStates, 0);
              },
            },
            {
              svg: '/assets/svg/allineamento-testo-sinistra.svg',
              className: 'btn-text-control btn-text-align btn-text-align-left',
              onClick: () => {
                setActive();
                editorSetTextAlign('left');
                setTimeout(updateActiveStates, 0);
              },
            },
            {
              svg: '/assets/svg/allineamento-testo-centro.svg',
              className:
                'btn-text-control btn-text-align btn-text-align-center',
              onClick: () => {
                setActive();
                editorSetTextAlign('center');
                setTimeout(updateActiveStates, 0);
              },
            },
            {
              svg: '/assets/svg/allineamento-testo-destra.svg',
              className: 'btn-text-control btn-text-align btn-text-align-right',
              onClick: () => {
                setActive();
                editorSetTextAlign('right');
                setTimeout(updateActiveStates, 0);
              },
            },
            {
              svg: '/assets/svg/allineamento-testo-giustificato.svg',
              className:
                'btn-text-control btn-text-align btn-text-align-justify',
              onClick: () => {
                setActive();
                editorSetTextAlign('justify');
                setTimeout(updateActiveStates, 0);
              },
            },
            {
              svg: '/assets/svg/testo-grassetto.svg',
              className: 'btn-text-control btn-text-bold',
              onClick: () => {
                setActive();
                toggleFontWeightBold();
                updateActiveStates();
              },
            },
            {
              svg: '/assets/svg/test-sottolineato.svg',
              className: 'btn-text-control btn-text-underline',
              onClick: () => {
                setActive();
                toggleTextUnderline();
                updateActiveStates();
              },
            },
            {
              svg: '/assets/svg/testo-corsivo.svg',
              className: 'btn-text-control btn-text-italic',
              onClick: () => {
                setActive();
                toggleFontStlyeItalic();
                updateActiveStates();
              },
            },
            // Spacing moved inside the three-dots menu as "Interlinea"
            {
              inlineSvg:
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="M5 14a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm7 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm7 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"></path></svg>',
              className: 'btn-text-control',
              hideArrow: true,
              children: (() => {
                const children = [];
                // Interlinea with vertical (line-height) and horizontal (letter spacing) sliders
                children.push({
                  text: 'Interlinea',
                  svg: '/assets/svg/spaziatura-testi.svg',
                  className: 'btn-customize',
                  children: [
                    {
                      type: 'control',
                      className: 'text-spacing-v-control',
                      render: (row, object) => {
                        try {
                          row.classList.add('floating-control-row');
                          const icon = document.createElement('div');
                          icon.className = 'control-icon';
                          const img = document.createElement('img');
                          img.className = 'inject-me no-fill';
                          img.setAttribute(
                            'data-src',
                            '/assets/svg/img-interlinea-verticale.svg'
                          );
                          icon.appendChild(img);
                          row.appendChild(icon);

                          const range = document.createElement('input');
                          range.type = 'range';
                          range.className = 'control-range';
                          range.min = '0.5';
                          range.max = '5';
                          range.step = '0.01';
                          const initial =
                            typeof object.lineHeight === 'number' &&
                            object.lineHeight
                              ? object.lineHeight
                              : 1.0;
                          range.value = String(initial);
                          row.appendChild(range);

                          const num = document.createElement('input');
                          num.type = 'number';
                          num.className = 'control-number';
                          num.min = '0.5';
                          num.max = '5';
                          num.step = '0.01';
                          num.value = String(initial);
                          row.appendChild(num);

                          const apply = (val, saveHistory = false) => {
                            const v = Math.max(
                              0.5,
                              Math.min(5, parseFloat(val))
                            );
                            range.value = String(v);
                            num.value = String(v);
                            try {
                              object.set('lineHeight', v);
                              object.set('dirty', true);
                              if (window.canvas) window.canvas.renderAll();
                              if (saveHistory) {
                                const {
                                  triggerStateChangedRequest,
                                } = require('~core');
                                triggerStateChangedRequest();
                              }
                            } catch (e) {
                              void e;
                            }
                          };

                          range.addEventListener('input', () =>
                            apply(range.value, false)
                          );
                          range.addEventListener('change', () =>
                            apply(range.value, true)
                          );
                          num.addEventListener('input', () =>
                            apply(num.value, false)
                          );
                          num.addEventListener('change', () =>
                            apply(num.value, true)
                          );

                          return row;
                        } catch (e) {
                          void e;
                          return row;
                        }
                      },
                    },
                    {
                      type: 'control',
                      className: 'text-spacing-h-control',
                      render: (row, object) => {
                        try {
                          row.classList.add('floating-control-row');
                          const icon = document.createElement('div');
                          icon.className = 'control-icon';
                          const img = document.createElement('img');
                          img.className = 'inject-me no-fill';
                          img.setAttribute(
                            'data-src',
                            '/assets/svg/img-interlinea-orizzontale.svg'
                          );
                          icon.appendChild(img);
                          row.appendChild(icon);

                          const range = document.createElement('input');
                          range.type = 'range';
                          range.className = 'control-range';
                          range.min = '-200';
                          range.max = '1000';
                          range.step = '1';
                          const initial =
                            typeof object.charSpacing === 'number'
                              ? object.charSpacing
                              : 0;
                          range.value = String(initial);
                          row.appendChild(range);

                          const num = document.createElement('input');
                          num.type = 'number';
                          num.className = 'control-number';
                          num.min = '-200';
                          num.max = '1000';
                          num.step = '1';
                          num.value = String(initial);
                          row.appendChild(num);

                          const apply = (val, saveHistory = false) => {
                            let v = parseFloat(val);
                            if (isNaN(v)) v = 0;
                            v = Math.max(-200, Math.min(1000, v));
                            range.value = String(v);
                            num.value = String(v);
                            try {
                              object.set('charSpacing', v);
                              object.set('dirty', true);
                              if (window.canvas) window.canvas.renderAll();
                              if (saveHistory) {
                                const {
                                  triggerStateChangedRequest,
                                } = require('~core');
                                triggerStateChangedRequest();
                              }
                            } catch (e) {
                              void e;
                            }
                          };

                          range.addEventListener('input', () =>
                            apply(range.value, false)
                          );
                          range.addEventListener('change', () =>
                            apply(range.value, true)
                          );
                          num.addEventListener('input', () =>
                            apply(num.value, false)
                          );
                          num.addEventListener('change', () =>
                            apply(num.value, true)
                          );

                          return row;
                        } catch (e) {
                          void e;
                          return row;
                        }
                      },
                    },
                  ],
                });

                try {
                  const more = buildObjectActionsButtons(() => textbox) || [];
                  return children.concat(more);
                } catch (e) {
                  return children;
                }
              })(),
            },
          ],
        },
      },
    };

    control = manager.createFloatingControls(textbox, options);

    // Initial state
    setTimeout(updateActiveStates, 0);
    // Refresh on common canvas events (including text selection changes)
    try {
      const update = () => setTimeout(updateActiveStates, 0);
      window.canvas.on('selection:created', update);
      window.canvas.on('selection:updated', update);
      window.canvas.on('text:selection:changed', update);
      window.canvas.on('custom:state:changed', update);
      window.canvas.on('object:modified', update);
      textbox.on('changed', update);
    } catch (e) {
      console.error('Failed wiring text floating control events', e);
    }
    return control;
  },
});
